"use client";

import { Box, Button, Flex, Text, Tooltip } from "@radix-ui/themes";
import _ from "lodash";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useCommSettingsStore } from "@/app/voice-settings";
import { usePatchVoiceSettings } from "@/app/voice-settings/[tn]/utils";
import { CommunicationSettings, DoNotDisturbCallHandlingEnum, DoNotDisturbStatusEnum } from "@/clients/ntas/basic-selfcare";
import AriaToaster from "@/components/AriaToaster";
import { MoonIconNoDots } from "@/public/index";
import useTNStore from "@/stores/TNStore";

export default function DndToggle() {
    const [activeView, setActiveView] = useState<DoNotDisturbStatusEnum>(DoNotDisturbStatusEnum.Off);

    const { tn } = useTNStore();
    const { commSettings, setCommSettings, deltaForPatch, setDeltaForPatch } = useCommSettingsStore();
    const { mutate: patchCommSettings, isPending } = usePatchVoiceSettings(
        tn,
        setDeltaForPatch,
        `"Do Not Disturb" mode has been successfully ${activeView === DoNotDisturbStatusEnum.On ? "activated" : "deactivated"}.`,
    );
    const [isBlockingAllIncoming, setIsBlockingAllIncoming] = useState<boolean>(_.get(commSettings, "callBlocking.blkAll") ? true : false);

    useEffect(() => {
        if (commSettings?.doNotDisturb && !deltaForPatch?.doNotDisturb) setActiveView(_.get(commSettings, "doNotDisturb.status") || DoNotDisturbStatusEnum.Off);
        setIsBlockingAllIncoming(_.get(commSettings, "callBlocking.blkAll") ? true : false);
    }, [commSettings, commSettings.doNotDisturb, deltaForPatch, deltaForPatch.doNotDisturb]);

    const handleDndToggle = () => {
        const checked = activeView === DoNotDisturbStatusEnum.On ? DoNotDisturbStatusEnum.Off : DoNotDisturbStatusEnum.On;
        setActiveView(checked);

        const tmpComSettings = _.setWith(commSettings, "doNotDisturb.status", checked, _.clone);
        const tmpDeltaForPatch: CommunicationSettings = _.cloneDeep(deltaForPatch);

        const featureSettings = _.cloneDeep(_.get(commSettings, "doNotDisturb")) || {};
        featureSettings.status = checked;
        _.setWith(tmpDeltaForPatch, "doNotDisturb", featureSettings, _.cloneDeep);

        setCommSettings(_.setWith(tmpComSettings, "doNotDisturb.callHandling", DoNotDisturbCallHandlingEnum.Busy, _.clone));
        setDeltaForPatch(_.setWith(tmpDeltaForPatch, "doNotDisturb.callHandling", DoNotDisturbCallHandlingEnum.Busy, _.clone));

        patchCommSettings({ communicationSettings: tmpDeltaForPatch });
    };

    const isOn = activeView === DoNotDisturbStatusEnum.On;

    return (
        tn &&
        tn.length === 11 && (
            <>
                <AriaToaster />
                <Tooltip
                    className="w-[327px]"
                    side="top"
                    content={
                        !isBlockingAllIncoming ? (
                            <ReactMarkdown>
                                Turn On Do Not Disturb to ensure all incoming calls receive a busy tone. All calls will be redirected to the destination
                                specified under the **&apos;Forward incoming calls when the line is busy&apos;** setting found in Voice Settings &gt; Call
                                Forwarding. All calls will be logged in Call History.
                            </ReactMarkdown>
                        ) : (
                            <ReactMarkdown>
                                Enable Do Not Disturb to ensure all incoming calls receive a busy tone. All calls will be logged in Call History.
                            </ReactMarkdown>
                        )
                    }>
                    <Button
                        disabled={isPending || isBlockingAllIncoming}
                        onClick={handleDndToggle}
                        variant={isOn ? "solid" : "outline"}
                        className={`h-16 rounded-xl ${isOn && "bg-att-cobalt-blue"} ${!isPending && !isBlockingAllIncoming && "cursor-pointer"}  ${isBlockingAllIncoming && "opacity-50"}`}>
                        <Flex align="start" justify="between" className="w-[150px] p-3">
                            <MoonIconNoDots className={`inline-block ${isOn ? "text-white" : "text-black"}`} />
                            <Box className={isOn ? "text-white" : "text-mako-gray"}>
                                <Text as="p" className="text-sm font-medium">
                                    Do Not Disturb
                                </Text>
                                <Text as="p" className="text-xs">
                                    {isOn ? "On" : "Off"}
                                </Text>
                            </Box>
                        </Flex>
                    </Button>
                </Tooltip>
            </>
        )
    );
}
